import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";
import { useHistory } from "react-router-dom";

import { getFileUrl } from "../../core/config/api.js";
import { baseSymbol } from "../../core/currency";
import MoneyRange from "../../components/formatters/MoneyRange";
import { Toggle } from "../../components/ui";
import AlertHelper from "../../helpers/Alert";
import RCAlertDetailModal from "./RCAlertDetailModal";
import "./rc-alert-card.scss";

const IEdit = () => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path
      d="M9.5 1.5L12.5 4.5L5 12H2V9L9.5 1.5Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

const IArrow = () => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path
      d="M3 7h8M8 4l3 3-3 3"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RCAlertCard = ({ alert, handleItemUpdate }) => {
  const history = useHistory();
  // Named `toast`, not `alert` — the buy alert record itself is called
  // `alert`, so reusing react-alert's conventional variable name here would
  // silently shadow it.
  const toast = useAlert();
  const { showApiError } = useApiError();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Optimistic toggle state with rollback-on-failure — the legacy AlertItem
  // fired these PATCHes and forgot about them, so a failed request left the
  // pill showing a state the backend never saved. Both toggles below revert
  // and toast on error instead.
  const [status, setStatus] = useState(!!alert.status);
  const [alertOn, setAlertOn] = useState(!!alert.alert);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!alert) return null;

  const product = alert.product;
  // Brand-only / keyword alerts (is_not_product) may have no product object
  // at all — the legacy row read alert.product.brand.name unconditionally
  // and crashed on these. Every product field below is optional-chained.
  const brandName = product?.brand?.name;
  const modelName = product?.model?.name;
  const displayName =
    [brandName, modelName].filter(Boolean).join(" ") ||
    alert.keywords ||
    "Any product";

  const image =
    !alert.is_not_product && product?.images?.length > 0
      ? getFileUrl(product.images[0].id)
      : "/assets/icons/alert-watch.png";

  const yearLabel = AlertHelper.getYearRangeLabel(alert.years);

  const onEdit = (e) => {
    e.stopPropagation();
    history.push(`/alerts/${alert.id}/edit`);
  };

  const onDelete = async (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    try {
      await axios.delete(`/api/buy-alerts/${alert.id}`);
      handleItemUpdate && handleItemUpdate();
    } catch (error) {
      showApiError(error, "delete the alert");
    }
  };

  const onStatusChange = async (next) => {
    setStatus(next);
    try {
      await axios.patch(`/api/buy-alerts/${alert.id}/status`, {
        status: next,
      });
    } catch (error) {
      setStatus(!next);
      showApiError(error, "update the alert status");
    }
  };

  const onAlertToggle = async (next) => {
    setAlertOn(next);
    try {
      await axios.patch(`/api/buy-alerts/${alert.id}/alert`, {
        alert: next,
      });
    } catch (error) {
      setAlertOn(!next);
      showApiError(error, "update the alert notifications");
    }
  };

  // RCAlertDetailModal renders via react-modal's portal, appended to
  // document.body — outside this div's DOM subtree, but still a React
  // child of it. React's synthetic events bubble the component tree, not
  // the DOM tree, so a click on the modal's close button / overlay still
  // reaches this handler in the same batch and reopens what it just closed.
  // Guarding on real DOM containment excludes those portaled clicks while
  // still opening on any genuine click inside the card itself.
  const onCardClick = (e) => {
    if (e.currentTarget.contains(e.target)) setDetailOpen(true);
  };

  return (
    <div className="rc-acard" onClick={onCardClick}>
      <div className="rc-acard__info">
        <div className="rc-acard__meta-row">
          {!alert.is_not_product && alert.reference_number && (
            <span className="rc-acard__meta-item">
              {alert.reference_number}
            </span>
          )}
        </div>

        <div className="rc-acard__name-row">
          <span className="rc-acard__name">{displayName}</span>
          {alert.underpriced && (
            <img
              src="/assets/icons/diamond.svg"
              alt="Underpriced"
              className="rc-acard__diamond"
              title="Alerts on underpriced listings"
            />
          )}
        </div>

        <MoneyRange
          start={alert.min_price}
          end={alert.max_price}
          currency={baseSymbol()}
          className="rc-acard__price"
        />

        {yearLabel && (
          <div className="rc-acard__meta-row rc-acard__meta-row--year">
            <span className="rc-acard__meta-item">{yearLabel}</span>
          </div>
        )}
      </div>

      <div className="rc-acard__image-area">
        <img src={image} alt={displayName} className="rc-acard__image" />

        <div
          className="rc-acard__top-right"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="rc-acard__overlay-btn"
            onClick={onEdit}
            title="Edit"
          >
            <IEdit />
          </button>
          <div className="rc-acard__action-wrap" ref={menuRef}>
            <button
              className="rc-acard__overlay-btn rc-acard__overlay-btn--actions"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              title="Actions"
            >
              <IArrow />
            </button>
            {menuOpen && (
              <div
                className="rc-acard__action-menu"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="rc-acard__action-item rc-acard__action-item--danger"
                  onClick={onDelete}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="rc-acard__status-bar"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rc-acard__toggle-row">
          <span className="rc-acard__toggle-label">
            {status ? "ON" : "OFF"}
          </span>
          <Toggle bare size="md" checked={status} onChange={onStatusChange} tone="active" />
        </div>
        <div className="rc-acard__toggle-row">
          <span className="rc-acard__toggle-label">ALERT</span>
          <Toggle bare size="md" checked={alertOn} onChange={onAlertToggle} />
        </div>
      </div>

      {/* Always mounted, never `{detailOpen && ...}` — react-modal only
          removes its `ReactModal__Body--open` scroll-lock class through the
          isOpen:true->false prop transition on componentDidUpdate. Unmounting
          the whole tree instead (as this used to) skips that path, leaking
          the class and locking page scroll after every close. Content stays
          unmounted while closed regardless — ModalPortal.render() returns
          null until isOpen is true, so AlertListingTable still isn't fetched
          for cards that have never been opened. */}
      <RCAlertDetailModal
        alertId={alert.id}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
};

export default RCAlertCard;
